import { apiV1 } from "api-v2";

export interface ProjectLeadInfo {
	id: number;
	name: string;
	email: string;
}

export interface ProjectSector {
	id: number;
	name: string;
}

export interface ShortlistProject {
	id: number;
	uuid: string;
	name: string;
	location: string | null;
	description: string | null;
	project_lead: ProjectLeadInfo | null;
	sectors: ProjectSector[];
}

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export const getShortlistProjects = async (): Promise<ShortlistProject[]> => {
	const response = await apiV1.get("reports/shortlist/projects");
	return unwrap<ShortlistProject[]>(response.data);
};

/* ---- Available sections metadata ---- */

export interface SectionFieldMeta {
	field: string;
	label: string;
}

export interface SectionMeta {
	key: string;
	title: string;
	type: string;
	fields?: SectionFieldMeta[];
}

export const getAvailableSections = async (): Promise<SectionMeta[]> => {
	const response = await apiV1.get("reports/shortlist/available-sections");
	return unwrap<SectionMeta[]>(response.data);
};

/* ---- Live preview HTML ---- */

export interface PreviewPageHtml {
	label: string;
	html: string;
}

export interface ShortlistPreviewHtmlResponse {
	pages: PreviewPageHtml[];
	header_html: string | null;
	footer_html: string | null;
	cover_html: string | null;
	additional_text_html: string | null;
	project_details_html: string | null;
	design_schema: Record<string, unknown>;
}

export interface ShortlistPreviewHtmlParams {
	visible_sections?: string[];
	visible_fields?: Record<string, string[]>;
	design_schema?: Record<string, unknown>;
	company_ids?: number[];
	search_sector_ids?: number[];
	group_id?: number | string;
	additional_text?: string;
	project_id?: number | null;
	project_description_override?: string;
	include_project_details?: boolean;
	project_visible_fields?: Record<string, boolean>;
	cover_project_name?: string;
	cover_supplier_role?: string;
	cover_presented_to?: string;
	cover_presented_to_role?: string;
	cover_show_logo?: boolean | null;
	cover_created_by_role?: string;
	cover_page_mode?: string;
}

export const getShortlistPreviewHtml = async (
	params: ShortlistPreviewHtmlParams
): Promise<ShortlistPreviewHtmlResponse> => {
	const response = await apiV1.post("reports/shortlist/preview-html", params);
	return unwrap<ShortlistPreviewHtmlResponse>(response.data);
};

/* ---- User presets CRUD ---- */

export interface ShortlistPreset {
	id: number;
	uuid: string;
	user_id: number;
	company_id: number;
	settings_type: string;
	name: string;
	description: string | null;
	settings_snapshot: Record<string, unknown>;
	include_cover_page: boolean;
	cover_page_file_id: number | null;
	cover_page: { file_name: string; original_filename: string | null } | null;
	include_additional_text: boolean;
	additional_text: string | null;
	created_at: string;
	updated_at: string;
}

export interface CreatePresetPayload {
	name: string;
	description?: string;
	settings_type: string;
	settings_snapshot: Record<string, unknown>;
	include_cover_page?: boolean;
	cover_page_file_id?: number | null;
	include_additional_text?: boolean;
	additional_text?: string | null;
}

export type UpdatePresetPayload = Partial<CreatePresetPayload>;

const PRESETS_BASE = "reports/shortlist/presets";

export const getShortlistPresets = async (): Promise<ShortlistPreset[]> => {
	const response = await apiV1.get(PRESETS_BASE);
	return unwrap<ShortlistPreset[]>(response.data);
};

export const getShortlistPreset = async (uuid: string): Promise<ShortlistPreset> => {
	const response = await apiV1.get(`${PRESETS_BASE}/${uuid}`);
	return unwrap<ShortlistPreset>(response.data);
};

export const createShortlistPreset = async (
	payload: CreatePresetPayload
): Promise<ShortlistPreset> => {
	const response = await apiV1.post(PRESETS_BASE, payload);
	return unwrap<ShortlistPreset>(response.data);
};

export const updateShortlistPreset = async (
	uuid: string,
	payload: UpdatePresetPayload
): Promise<ShortlistPreset> => {
	const response = await apiV1.put(`${PRESETS_BASE}/${uuid}`, payload);
	return unwrap<ShortlistPreset>(response.data);
};

export const deleteShortlistPreset = async (uuid: string): Promise<void> => {
	await apiV1.delete(`${PRESETS_BASE}/${uuid}`);
};
